import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScanFace, Camera, CheckCircle2, XCircle, Loader2, DoorOpen } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { loadFaceModels, detectFaceDescriptor } from "@/lib/faceapi";
import { useAuth } from "@/lib/auth";

export default function Turnstile() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [logs, setLogs] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [modelsReady, setModelsReady] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [manualId, setManualId] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const load = async () => {
    const [s, l, u] = await Promise.all([api.get("/students"), api.get("/attendance?limit=15"), api.get("/units")]);
    setStudents(s.data); setLogs(l.data); setUnits(u.data);
    if (!selectedUnit) setSelectedUnit(user?.unit_id || u.data[0]?.id || "");
  };
  useEffect(() => {
    load();
    loadFaceModels().then(() => setModelsReady(true)).catch(() => {});
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setStreaming(true);
    } catch { toast.error("Câmera não disponível"); }
  };
  const stop = () => { streamRef.current?.getTracks().forEach(t=>t.stop()); streamRef.current=null; setStreaming(false); };

  const scan = async () => {
    if (!videoRef.current) return;
    setScanning(true);
    try {
      const res = await detectFaceDescriptor(videoRef.current);
      if (!res) { toast.error("Nenhum rosto detectado"); setScanning(false); return; }
      const { data } = await api.post("/turnstile/check", { face_embedding: res.descriptor, unit_id: selectedUnit });
      setLastResult(data);
      if (data.granted) toast.success(`Acesso Liberado — ${data.student.name}`);
      else toast.error("Acesso Negado");
      load();
    } catch { toast.error("Erro na leitura"); }
    finally { setScanning(false); }
  };

  const manualGrant = async () => {
    if (!manualId) return;
    try {
      const { data } = await api.post(`/turnstile/manual/${manualId}?unit_id=${selectedUnit}`);
      setLastResult({ granted: true, confidence: 1, student: data.student });
      toast.success(`Liberado — ${data.student.name}`);
      load();
    } catch { toast.error("Erro"); }
  };

  const [generalCount, setGeneralCount] = useState(0);
  const loadGeneralStats = async () => {
    try {
      const { data } = await api.get("/turnstile/general-release/stats?days=1");
      setGeneralCount(data.total || 0);
    } catch {}
  };
  useEffect(() => { loadGeneralStats(); }, []);

  const generalRelease = async () => {
    if (!window.confirm("Liberar a catraca para QUALQUER pessoa? A ação é registrada na auditoria.")) return;
    try {
      const { data } = await api.post("/turnstile/general-release", { unit_id: selectedUnit });
      setLastResult({ granted: true, confidence: 1, student: { name: "Liberação geral" }, method: "general_release" });
      toast.success("Catraca liberada!");
      load();
      loadGeneralStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const currentUnit = units.find(u => u.id === selectedUnit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Catraca Biométrica</h1>
          <p className="text-slate-500">Reconhecimento facial com face-api.js — acesso multiunidade</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-slate-500">Unidade</span>
          <Select value={selectedUnit} onValueChange={setSelectedUnit}>
            <SelectTrigger className="w-52" data-testid="turnstile-unit-select"><SelectValue/></SelectTrigger>
            <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6 border border-slate-200 bg-slate-950 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ScanFace className="w-5 h-5 text-rose-500"/>
              <h3 className="font-heading text-lg font-bold">Leitor</h3>
            </div>
            <span className="text-xs text-rose-400">{currentUnit?.name || "—"}</span>
          </div>
          <div className="relative aspect-video bg-black rounded-xl overflow-hidden mb-4" data-testid="turnstile-camera-stream">
            <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover"/>
            {streaming && <div className="absolute inset-12 border-4 rounded-2xl face-frame pointer-events-none"/>}
            {!streaming && <div className="absolute inset-0 flex items-center justify-center text-slate-500"><Camera className="w-12 h-12"/></div>}
          </div>
          <canvas ref={canvasRef} className="hidden"/>
          <div className="flex gap-2">
            {!modelsReady ? (
              <Button disabled className="flex-1 bg-slate-800"><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Carregando IA...</Button>
            ) : !streaming ? (
              <Button onClick={start} data-testid="turnstile-simulator-trigger" className="flex-1 bg-rose-600 hover:bg-rose-700"><Camera className="w-4 h-4 mr-2"/>Iniciar leitor</Button>
            ) : (
              <>
                <Button onClick={scan} disabled={scanning} className="flex-1 bg-rose-600 hover:bg-rose-700">
                  {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Lendo...</> : "Ler face"}
                </Button>
                <Button onClick={stop} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-900">Parar</Button>
              </>
            )}
          </div>

          <div className="mt-4 p-3 rounded-lg border border-slate-800 bg-slate-900">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Liberação manual</div>
            <div className="flex gap-2">
              <Select value={manualId} onValueChange={setManualId}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white"><SelectValue placeholder="Selecionar aluno"/></SelectTrigger>
                <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={manualGrant} data-testid="turnstile-manual-override-button" className="bg-emerald-600 hover:bg-emerald-700 shrink-0">Liberar</Button>
            </div>
          </div>

          <div className="mt-3 p-3 rounded-lg border-2 border-dashed border-amber-500/40 bg-amber-500/5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-bold flex items-center gap-1"><DoorOpen className="w-3 h-3"/>Liberação geral (qualquer pessoa)</div>
              <span className="text-[10px] text-amber-300/80" data-testid="turnstile-general-count">Hoje: <strong>{generalCount}</strong></span>
            </div>
            <Button onClick={generalRelease} data-testid="turnstile-general-release-button" className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase">
              <DoorOpen className="w-4 h-4 mr-2"/>Liberar catraca agora
            </Button>
            <p className="text-[10px] text-amber-300/70 mt-2 leading-relaxed">Ação registrada com nome do operador e horário. Ver estatísticas em <strong>Relatórios → Liberação Geral</strong>.</p>
          </div>
        </Card>

        <div className="space-y-4">
          {lastResult && (
            <Card className={`p-6 border-2 ${lastResult.granted ? "border-emerald-500 bg-emerald-50" : "border-rose-500 bg-rose-50"}`}
              data-testid={lastResult.granted ? "turnstile-access-granted-alert" : "turnstile-access-denied-alert"}>
              <div className="flex items-center gap-3">
                {lastResult.granted ? <CheckCircle2 className="w-10 h-10 text-emerald-600"/> : <XCircle className="w-10 h-10 text-rose-600"/>}
                <div className="flex-1">
                  <div className={`font-heading text-2xl font-bold ${lastResult.granted ? "text-emerald-900" : "text-rose-900"}`}>
                    {lastResult.granted ? "Acesso Liberado" : "Acesso Negado"}
                  </div>
                  {lastResult.student && <div className="text-sm text-slate-700 font-medium">{lastResult.student.name}</div>}
                  {!lastResult.granted && lastResult.reason && (
                    <div className="mt-1 text-sm font-bold text-rose-900 uppercase" data-testid="turnstile-denial-reason">{lastResult.reason}</div>
                  )}
                  <div className="text-xs font-mono text-slate-600 mt-1">
                    Confiança: {(lastResult.confidence * 100).toFixed(1)}% · Distância: {lastResult.distance}
                  </div>
                  {lastResult.student?.home_unit_id && lastResult.student.home_unit_id !== selectedUnit && (
                    <div className="text-xs mt-1 text-amber-700 font-semibold">Aluno de outra unidade — acesso cruzado</div>
                  )}
                </div>
              </div>
            </Card>
          )}

          <Card className="p-5 border border-slate-200" data-testid="turnstile-log-table">
            <h3 className="font-heading text-lg font-bold text-slate-900 mb-3">Log de acessos</h3>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {logs.map(l => (
                <div key={l.id} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
                  <div>
                    <span className="font-medium text-slate-900">{l.student_name}</span>
                    <span className="text-xs text-slate-500 ml-2">{new Date(l.timestamp).toLocaleString("pt-BR")}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${l.granted ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{l.granted ? "✓" : "✗"}</span>
                </div>
              ))}
              {logs.length === 0 && <p className="text-slate-500 text-sm text-center py-6">Sem acessos ainda</p>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
